import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';

import { SnackbarService } from 'src/app/shared/services/snackbar.service';
import { AuthenticationService } from 'src/app/shared/services/authentication.service';
import {
  ILigneResponse,
  IEvaluationResponse,
  INoteUpsertRequest,
  NoteType,
  INoteResponse,
  IMoisResponse
} from 'src/app/shared/models/evaluation';
import { AddLignesDialogComponent } from '../add-ligne-dialog/add-lignes-dialog.component';
import { EvaluationService } from '../evaluation-service';
import { ConfirmDialogComponent } from './confirm-dialog/confirm-dialog.component';

import { MatDatepickerInputEvent } from '@angular/material/datepicker';

export interface NoteChangeEvent {
  valeur: number | null;
  nonApplicable: boolean;
}

interface TableRowVm {
  ligne: ILigneResponse;
  categorie: string;
  showCategorie: boolean;
}

@Component({
  selector: 'app-evaluation-table',
  templateUrl: './evaluation-table.component.html',
  styleUrls: ['./evaluation-table.component.less']
})
export class EvaluationTableComponent implements OnInit {

  evaluation: IEvaluationResponse | null = null;

  isSaving = false;
  isAdmin = false;

  commentaireEdit = '';
  isSavingComment = false;

  pendingNotes = new Map<string, INoteUpsertRequest>();

  tableRows: TableRowVm[] = [];
  displayedColumns: string[] = [];
  private readonly baseColumns = ['categorie', 'critere', 'cible', 'demarrage', 'fin'];

  @ViewChild('tableWrapper') tableWrapper!: ElementRef<HTMLElement>;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private evaluationService: EvaluationService,
    private snackbarService: SnackbarService,
    private authService: AuthenticationService
  ) {}

  ngOnInit(): void {
    this.isAdmin = this.authService.isUserAdmin();
    const id = this.route.snapshot.paramMap.get('id');
    if (id) this.loadEvaluation(id);
  }

  loadEvaluation(id: string): void {
    this.evaluationService.getById(id).subscribe({
      next: (data) => {
        this.applyEvaluation(data);
      },
      error: (err) => {
        this.snackbarService.openSnackBar(
          this.extractErrorMessage(err, 'Erreur lors du chargement'),
          'error-snack-bar'
        );
      }
    });
  }

  private applyEvaluation(data: IEvaluationResponse): void {
    this.evaluation = data;
    this.commentaireEdit = data.commentaire ?? '';
    this.tableRows = this.buildTableRows(data.lignes);

    const moisColumns = data.mois.map(m => this.getMoisColumnId(m));
    this.displayedColumns = [...this.baseColumns, ...moisColumns];
  }

  private buildTableRows(lignes: ILigneResponse[]): TableRowVm[] {
    const grouped = new Map<string, ILigneResponse[]>();

    for (const ligne of lignes) {
      const categorie = ligne.categorieSnapshot;
      if (!grouped.has(categorie)) grouped.set(categorie, []);
      grouped.get(categorie)!.push(ligne);
    }

    const rows: TableRowVm[] = [];
    for (const [categorie, lignesCategorie] of grouped.entries()) {
      lignesCategorie.forEach((ligne, idx) => {
        rows.push({
          ligne,
          categorie,
          showCategorie: idx === 0
        });
      });
    }

    return rows;
  }

  getMoisList(): IMoisResponse[] {
    return this.evaluation?.mois ?? [];
  }

  trackByMoisId(_: number, mois: IMoisResponse): string {
    return mois.id;
  }

  getMoisColumnId(mois: IMoisResponse): string {
    return `mois_${mois.moisIndex}`;
  }

  enregistrerCommentaire(): void {
    if (!this.evaluation) return;
    this.isSavingComment = true;

    this.evaluationService.update(this.evaluation.id, { commentaire: this.commentaireEdit }).subscribe({
      next: (updated) => {
        this.applyEvaluation(updated);
        this.isSavingComment = false;
        this.snackbarService.openSnackBar('Commentaire enregistré');
      },
      error: (err) => {
        this.snackbarService.openSnackBar(
          this.extractErrorMessage(err, 'Erreur lors de l\'enregistrement du commentaire'),
          'error-snack-bar'
        );
        this.isSavingComment = false;
      }
    });
  }

  getCivilite(): string {
    const prenomsFeminins = ['marie', 'sophie', 'julie', 'claire', 'anne', 'emma', 'lea', 'sarah', 'camille', 'laura'];
    const prenom = (this.evaluation?.prenom ?? '').toLowerCase().trim();
    return prenomsFeminins.some(p => prenom.startsWith(p)) ? 'Mme' : 'M.';
  }

  scrollToMois(moisIndex: number): void {
    const el = document.getElementById(`col-mois-${moisIndex}`);
    if (el && this.tableWrapper?.nativeElement) {
      const wrapper = this.tableWrapper.nativeElement;
      const elLeft = el.offsetLeft;
      wrapper.scrollTo({ left: elLeft - 360, behavior: 'smooth' });
    }
  }

  getNote(ligne: ILigneResponse, type: NoteType): INoteResponse | null {
    return ligne.notes.find(n => n.type === type && !n.moisId) ?? null;
  }

  getNoteMensuelle(ligne: ILigneResponse, moisId: string): INoteResponse | null {
    return ligne.notes.find(n => n.type === 'MENSUELLE' && n.moisId === moisId) ?? null;
  }

  onNoteChange(
    ligne: ILigneResponse,
    type: NoteType,
    moisId: string | null,
    event: NoteChangeEvent
  ): void {
    if (!this.evaluation || this.evaluation.status === 'ARCHIVEE') return;

    if (!this.isMoisModifiable(moisId)) {
      this.snackbarService.openSnackBar(
        'Ce mois est finalisé. Seuls les administrateurs peuvent le modifier.',
        'error-snack-bar'
      );
      return;
    }

    const key = `${ligne.id}_${type}_${moisId ?? ''}`;
    const noteReq: INoteUpsertRequest = {
      ligneId: ligne.id,
      type,
      moisId: moisId ?? undefined,
      valeur: event.valeur,
      nonApplicable: event.nonApplicable
    };

    this.pendingNotes.set(key, noteReq);
    this.updateNoteLocally(ligne, type, moisId, event);
  }

  private updateNoteLocally(
    ligne: ILigneResponse,
    type: NoteType,
    moisId: string | null,
    event: NoteChangeEvent
  ): void {
    const existingNote = moisId
      ? this.getNoteMensuelle(ligne, moisId)
      : this.getNote(ligne, type);

    if (existingNote) {
      existingNote.valeur = event.valeur;
      existingNote.nonApplicable = event.nonApplicable;
    } else {
      ligne.notes.push({
        id: 'temp_' + Date.now(),
        type,
        moisId,
        valeur: event.valeur,
        nonApplicable: event.nonApplicable
      });
    }
  }

  enregistrer(): void {
    if (!this.evaluation || this.pendingNotes.size === 0) return;

    this.isSaving = true;
    const notes = Array.from(this.pendingNotes.values());

    this.evaluationService.update(this.evaluation.id, { notes }).subscribe({
      next: (updated) => {
        this.applyEvaluation(updated);
        this.pendingNotes.clear();
        this.isSaving = false;
        this.snackbarService.openSnackBar('Notes enregistrées avec succès');
      },
      error: (err) => {
        this.snackbarService.openSnackBar(
          this.extractErrorMessage(err, 'Erreur lors de l\'enregistrement'),
          'error-snack-bar'
        );
        this.isSaving = false;
      }
    });
  }

  ouvrirAjoutCriteres(): void {
    if (!this.evaluation) return;

    const existingCompetenceIds = this.evaluation.lignes
      .map(l => l.competenceId)
      .filter((id): id is string => id !== null);

    const dialogRef = this.dialog.open(AddLignesDialogComponent, {
      width: '680px',
      maxWidth: '95vw',
      data: {
        evaluationId: this.evaluation.id,
        existingCompetenceIds
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.applyEvaluation(result);
      }
    });
  }

  canAddMois(): boolean {
    if (!this.evaluation) return false;
    if (this.evaluation.mois.length === 0) return true;

    const dernierMois = this.evaluation.mois[this.evaluation.mois.length - 1];
    return this.isMoisComplet(dernierMois);
  }

  isMoisComplet(mois: IMoisResponse): boolean {
    if (!this.evaluation) return false;
    return this.evaluation.lignes.every(l => {
      const note = this.getNoteMensuelle(l, mois.id);
      return !!note && (note.valeur !== null || note.nonApplicable);
    });
  }

  isMoisModifiable(moisId: string | null): boolean {
    if (this.isAdmin) return true;
    if (!moisId) return true;

    const mois = this.evaluation?.mois.find(m => m.id === moisId);
    if (!mois) return true;

    return !this.isMoisComplet(mois);
  }

  getMoisBlockedTooltip(): string {
    if (!this.evaluation || this.evaluation.mois.length === 0) return '';
    const dernierMois = this.evaluation.mois[this.evaluation.mois.length - 1];
    if (!this.isMoisComplet(dernierMois)) {
      return `Remplissez toutes les notes de M+${dernierMois.moisIndex} avant d'ajouter un nouveau mois`;
    }
    return '';
  }

  getMoisReadOnlyTooltip(moisId: string): string {
    if (this.isAdmin) return '';
    const mois = this.evaluation?.mois.find(m => m.id === moisId);
    if (mois && this.isMoisComplet(mois)) {
      return 'Ce mois est finalisé. Seuls les administrateurs peuvent le modifier.';
    }
    return '';
  }

  addMois(): void {
    if (!this.evaluation) return;

    if (this.pendingNotes.size > 0) {
      this.isSaving = true;
      const notes = Array.from(this.pendingNotes.values());

      this.evaluationService.update(this.evaluation.id, { notes }).subscribe({
        next: () => {
          this.pendingNotes.clear();
          this.lancerAddMois();
        },
        error: (err) => {
          this.snackbarService.openSnackBar(
            this.extractErrorMessage(err, 'Erreur lors de l\'enregistrement des notes'),
            'error-snack-bar'
          );
          this.isSaving = false;
        }
      });
    } else {
      this.lancerAddMois();
    }
  }

  private lancerAddMois(): void {
    if (!this.evaluation) return;
    this.isSaving = true;

    this.evaluationService.addMois(this.evaluation.id).subscribe({
      next: (updated) => {
        this.applyEvaluation(updated);
        this.isSaving = false;
        this.snackbarService.openSnackBar(`M${updated.mois.length} ajouté avec succès`);
        setTimeout(() => this.scrollToMois(updated.mois.length), 200);
      },
      error: (err) => {
        this.snackbarService.openSnackBar(
          this.extractErrorMessage(err, 'Erreur lors de l\'ajout du mois'),
          'error-snack-bar'
        );
        this.isSaving = false;
      }
    });
  }

   isEditingDateFmc = false;
dateFmcEdit: Date | null = null;

startEditDateFmc(): void {
  this.dateFmcEdit = this.evaluation?.dateFinMonteeCompetence
    ? new Date(this.evaluation.dateFinMonteeCompetence)
    : null;
  this.isEditingDateFmc = true;
}

cancelEditDateFmc(): void {
  this.isEditingDateFmc = false;
  this.dateFmcEdit = null;
}

saveDateFmc(): void {
  if (!this.evaluation || !this.dateFmcEdit) return;

  const d = this.dateFmcEdit;
  const dateIso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  this.isSaving = true;

  this.evaluationService.updatedfmc(this.evaluation.id, dateIso).subscribe({
    next: (updated) => {
      this.applyEvaluation(updated);
      this.isSaving = false;
      this.isEditingDateFmc = false;
      this.dateFmcEdit = null;
      this.snackbarService.openSnackBar('Date de fin de montée en compétences mise à jour');
    },
    error: (err) => {
      this.snackbarService.openSnackBar(
        this.extractErrorMessage(err, 'Erreur lors de la mise à jour de la date'),
        'error-snack-bar'
      );
      this.isSaving = false;
    }
  });
}

  canDeleteMois(mois: IMoisResponse): boolean {
    if (!this.evaluation) return false;
    return !this.evaluation.lignes.some(l =>
      l.notes.some(n => n.moisId === mois.id && (n.valeur !== null || n.nonApplicable))
    );
  }

  deleteMois(mois: IMoisResponse): void {
    if (!this.evaluation) return;

    const confirmRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Supprimer le mois',
        message: `Supprimer la colonne M+${mois.moisIndex} ? Cette action est irréversible.`,
        confirmLabel: 'Supprimer',
        confirmColor: 'warn'
      }
    });

    confirmRef.afterClosed().subscribe(confirmed => {
      if (!confirmed || !this.evaluation) return;
      this.isSaving = true;

      this.evaluationService.deleteMois(this.evaluation.id, mois.moisIndex).subscribe({
        next: (updated) => {
          this.applyEvaluation(updated);
          this.isSaving = false;
          this.snackbarService.openSnackBar(`M${mois.moisIndex} supprimé`);
        },
        error: (err) => {
          this.snackbarService.openSnackBar(
            this.extractErrorMessage(err, 'Erreur lors de la suppression'),
            'error-snack-bar'
          );
          this.isSaving = false;
        }
      });
    });
  }

  canValider(): boolean {
    if (!this.evaluation || this.evaluation.status !== 'BROUILLON') return false;

    for (const ligne of this.evaluation.lignes) {
      const demarrage = this.getNote(ligne, 'DEMARRAGE');
      if (!demarrage || (demarrage.valeur === null && !demarrage.nonApplicable)) return false;

      const cible = this.getNote(ligne, 'CIBLE');
      if (!cible || (cible.valeur === null && !cible.nonApplicable)) return false;

      for (const mois of this.evaluation.mois) {
        const noteMois = this.getNoteMensuelle(ligne, mois.id);
        if (!noteMois || (noteMois.valeur === null && !noteMois.nonApplicable)) return false;
      }
    }

    return true;
  }

  getValidationErrors(): string[] {
    if (!this.evaluation) return [];
    const errors: string[] = [];

    let missingDemarrage = 0;
    let missingCible = 0;
    const missingMois = new Set<number>();

    for (const ligne of this.evaluation.lignes) {
      const demarrage = this.getNote(ligne, 'DEMARRAGE');
      if (!demarrage || (demarrage.valeur === null && !demarrage.nonApplicable)) missingDemarrage++;

      const cible = this.getNote(ligne, 'CIBLE');
      if (!cible || (cible.valeur === null && !cible.nonApplicable)) missingCible++;

      for (const mois of this.evaluation.mois) {
        const noteMois = this.getNoteMensuelle(ligne, mois.id);
        if (!noteMois || (noteMois.valeur === null && !noteMois.nonApplicable)) {
          missingMois.add(mois.moisIndex);
        }
      }
    }

    if (missingDemarrage > 0) errors.push(`${missingDemarrage} critère(s) sans note Démarrage`);
    if (missingCible > 0) errors.push(`${missingCible} critère(s) sans note Cible`);
    if (missingMois.size > 0) errors.push(`Notes manquantes sur M${[...missingMois].join(', M')}`);

    return errors;
  }

  valider(): void {
    if (!this.evaluation) return;

    const confirmRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Valider l\'évaluation',
        message: 'Valider cette évaluation ? Cette action est irréversible.',
        confirmLabel: 'Valider',
        confirmColor: 'primary'
      }
    });

    confirmRef.afterClosed().subscribe(confirmed => {
      if (!confirmed || !this.evaluation) return;

      if (this.pendingNotes.size > 0) {
        this.isSaving = true;
        const notes = Array.from(this.pendingNotes.values());

        this.evaluationService.update(this.evaluation.id, { notes }).subscribe({
          next: (updated) => {
            this.applyEvaluation(updated);
            this.pendingNotes.clear();
            this.lancerValidation();
          },
          error: (err) => {
            this.snackbarService.openSnackBar(
              this.extractErrorMessage(err, 'Erreur lors de l\'enregistrement des notes'),
              'error-snack-bar'
            );
            this.isSaving = false;
          }
        });
      } else {
        this.lancerValidation();
      }
    });
  }

  private lancerValidation(): void {
    if (!this.evaluation) return;
    this.isSaving = true;

    this.evaluationService.validate(this.evaluation.id).subscribe({
      next: (updated) => {
        this.applyEvaluation(updated);
        this.isSaving = false;
        this.snackbarService.openSnackBar('Évaluation validée avec succès');
      },
      error: (err) => {
        this.snackbarService.openSnackBar(
          this.extractErrorMessage(err, 'Erreur lors de la validation'),
          'error-snack-bar'
        );
        this.isSaving = false;
      }
    });
  }

  archiver(): void {
    if (!this.evaluation) return;

    const confirmRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Archiver l\'évaluation',
        message: 'Archiver cette évaluation ? Elle sera en lecture seule définitivement.',
        confirmLabel: 'Archiver',
        confirmColor: 'warn'
      }
    });

    confirmRef.afterClosed().subscribe(confirmed => {
      if (!confirmed || !this.evaluation) return;
      this.isSaving = true;

      this.evaluationService.archive(this.evaluation.id).subscribe({
        next: (updated) => {
          this.applyEvaluation(updated);
          this.isSaving = false;
          this.snackbarService.openSnackBar('Évaluation archivée avec succès');
        },
        error: (err) => {
          this.snackbarService.openSnackBar(
            this.extractErrorMessage(err, 'Erreur lors de l\'archivage'),
            'error-snack-bar'
          );
          this.isSaving = false;
        }
      });
    });
  }

  goBack(): void {
    this.router.navigate(['/core/evaluations']);
  }

  private extractErrorMessage(err: any, fallback: string): string {
    const body = err?.error;
    if (body?.message && typeof body.message === 'string') return body.message;
    if (typeof body === 'string') return body;
    return fallback;
  }
}